import { gql } from "@apollo/client";
import { useCallback } from "react";
import { useMutation } from "@apollo/client/react";

import type {
  MenuItemDeleteResp,
  MenuItemDeleteVariables,
  MenuItem
} from "../../types/menuItemTypes";

const DELETE_MENU_ITEM = gql`
  mutation DeleteMenuItem($attributes: DeleteMenuItemInput!) {
    deleteMenuItem(attributes: $attributes) {
      menuItem {
        id
      }
      errors
    }
  }
`   

type UseDeleteMenuItemOptions = {
  onCompleted?: (menuItem: MenuItem) => void;
  onError?: (error: unknown) => void;
  mutationOptions?: Parameters<typeof useMutation<MenuItemDeleteResp, MenuItemDeleteVariables>>[1];
};

const useDeleteMenuItem = (options?: UseDeleteMenuItemOptions) => {
  const [mutate, { data, loading, error }] = useMutation<
    MenuItemDeleteResp,
    MenuItemDeleteVariables
  >(DELETE_MENU_ITEM, options?.mutationOptions);

  const deleteMenuItem = useCallback(
    async (input: MenuItemDeleteVariables['attributes']) => {
      try {
        const result = await mutate({
          variables: {
            attributes: {
              id: input.id!
            },
          },
        });

        const deleteMenuItem = result?.data?.deleteMenuItem;

        if (deleteMenuItem?.errors?.length) {
          throw new Error(deleteMenuItem.errors.join(", "));
        }

        if (options?.onCompleted && deleteMenuItem?.menuItem) {
          options.onCompleted(deleteMenuItem.menuItem);
        }

        return deleteMenuItem?.menuItem;
      } catch (err) {
        if (options?.onError) {
          options.onError(err);
        }
        throw err;
      }
    },
    [mutate, options]
  );

  const menuItem = data?.deleteMenuItem?.menuItem;

  return {
    deleteMenuItem,
    deletedMenuItem: menuItem,
    loading,
    error: error as Error | null | undefined,
  };
}

export default useDeleteMenuItem
